/**
 * jai1 tasks sync [--pull] [--push]
 * Bidirectional sync between cloud tasks and local tasks.jsonl
 *
 * --pull  : Download cloud tasks → overwrite local tasks.jsonl
 * --push  : Upload local tasks.jsonl → cloud
 * (none)  : Full merge — newest updated wins
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { TaskService } from '../../services/task.service.js';
import { SettingsService } from '../../services/settings.service.js';
import type { TaskSyncOptions } from '../../types/task.types.js';

export function createTaskSyncCommand(): Command {
    return new Command('sync')
        .description('Sync tasks between cloud and local (requires tasks.cloud = true)')
        .option('--pull', 'Pull: cloud → local (replaces tasks.jsonl)')
        .option('--push', 'Push: local → cloud (upserts all)')
        .option('-j, --json', 'Output JSON')
        .action(async (options: TaskSyncOptions & { json?: boolean }) => {
            const settings = new SettingsService();
            if (!settings.isTaskCloudEnabled()) {
                console.error(chalk.red('❌ tasks.cloud is not enabled in .jai1/settings.yaml'));
                console.error(chalk.dim('   Run: j settings set tasks.cloud true'));
                process.exit(1);
            }

            const service = new TaskService();
            // Wait for cloud provider to init
            await service.waitForInit();

            if (!service.isCloud) {
                console.error(chalk.red('❌ Cloud provider failed to initialize.'));
                console.error(chalk.dim('   Check that you are logged in: j auth'));
                process.exit(1);
            }

            const doPull = options.pull || (!options.pull && !options.push);
            const doPush = options.push || (!options.pull && !options.push);
            const isFull = doPull && doPush;

            if (isFull) {
                console.log(chalk.bold('🔄 Full sync: merging cloud ↔ local...'));
            } else if (doPull) {
                console.log(chalk.bold('⬇️  Pull: cloud → local...'));
            } else {
                console.log(chalk.bold('⬆️  Push: local → cloud...'));
            }

            try {
                if (!service.cloud?.pull || !service.cloud?.push) {
                    console.error(chalk.red('❌ Cloud provider does not support sync operations.'));
                    process.exit(1);
                }

                if (doPull) {
                    // Download all cloud tasks
                    const cloudTasks = await service.cloud.pull();
                    console.log(chalk.dim(`   ↓ ${cloudTasks.length} tasks fetched from cloud`));

                    if (isFull) {
                        // Full sync: merge by updated timestamp
                        // IMPORTANT: read from LOCAL file, not cloud provider
                        const localTasks = await service.readLocal();
                        console.log(chalk.dim(`   ← ${localTasks.length} tasks in local file`));
                        const localMap = new Map(localTasks.map((t) => [t.id, t]));
                        const cloudMap = new Map(cloudTasks.map((t) => [t.id, t]));

                        const merged = new Map<string, any>();
                        for (const [id, local] of localMap) merged.set(id, local);
                        for (const [id, cloud] of cloudMap) {
                            const local = merged.get(id);
                            if (!local || cloud.updated > local.updated) {
                                merged.set(id, cloud);
                            }
                        }

                        const mergedList = Array.from(merged.values());
                        await service.writeAll(mergedList);
                        console.log(chalk.green(`✅ Merged: ${mergedList.length} tasks`));

                        // Push merged back to cloud
                        console.log(chalk.dim(`   ↑ Pushing merged tasks to cloud...`));
                        await service.cloud.push(mergedList);
                        console.log(chalk.green(`✅ Full sync complete`));
                    } else {
                        // Pull only: overwrite local
                        await service.writeAll(cloudTasks);
                        console.log(chalk.green(`✅ Pull complete: ${cloudTasks.length} tasks written`));
                    }
                } else if (doPush) {
                    // Push only — read from LOCAL file, not cloud
                    const localTasks = await service.readLocal();
                    await service.cloud.push(localTasks);
                    console.log(chalk.green(`✅ Push complete: ${localTasks.length} tasks uploaded`));
                }
            } catch (error) {
                const msg = error instanceof Error ? error.message : String(error);
                if (options.json) {
                    console.log(JSON.stringify({ success: false, error: msg }));
                } else {
                    console.error(chalk.red(`❌ Sync failed: ${msg}`));
                }
                process.exit(1);
            }
        });
}
